import type { HotelsState } from "@b2bportal/core-hotels";
import {
  getHotelAvailabilityTrackingProperties,
  getHotelShopAvailabilityTrackingProperties,
  getHotelShopTrackingProperties,
} from "@b2bportal/core-hotels";
import { TripCategory } from "@b2bportal/core-types";
import {
  calculateDaysInAdvance,
  getDaysBetween,
} from "@b2bportal/core-utilities";
import { createSelector } from "@reduxjs/toolkit";
import {
  getRequestedSearch,
  getSearchFormDateRange,
  getSearchFormDestination,
  getSearchFormGuests,
  getSearchFormOrigin,
} from "../features/search/store/selectors";
import type { PackageState } from "../store";

/**
 * Combined state type that includes both Packages and Hotels state
 */
export type CombinedPackageHotelsState = PackageState & HotelsState;

/**
 * Get package-specific search properties for tracking
 */
export const getPackageSearchTrackingProperties = createSelector(
  [
    getRequestedSearch,
    getSearchFormOrigin,
    getSearchFormDestination,
    getSearchFormDateRange,
    getSearchFormGuests,
  ],
  (requestedSearch, origin, destination, dateRange, guests) => {
    const search = requestedSearch || {
      origin,
      destination,
      dateRange,
      guests,
    };

    return {
      origin: search.origin?.id?.code?.code ?? search.origin?.label,
      destination: search.destination?.label,
      adults_count: search.guests?.adultCount,
      children_count: search.guests?.childAges?.length || 0,
      infants_count:
        (search.guests?.infantOnLapAges?.length ?? 0) +
        (search.guests?.infantInSeatAges?.length ?? 0),
      trip_type: TripCategory.ROUND_TRIP, // Packages are always roundtrip
      los:
        dateRange.from != null && dateRange.until != null
          ? getDaysBetween(new Date(dateRange.from), new Date(dateRange.until))
          : undefined,
      advance:
        dateRange.from != null
          ? calculateDaysInAdvance(dateRange.from)
          : undefined,
    };
  }
);

/**
 * Get combined package + hotel availability tracking properties
 * Merges package-specific search data with hotel availability tracking data
 */
export const getPackageHotelAvailabilityTrackingProperties = (
  state: CombinedPackageHotelsState
) => {
  const hotelTracking = getHotelAvailabilityTrackingProperties(state);
  const packageSearch = getPackageSearchTrackingProperties(state);

  return {
    properties: {
      ...hotelTracking.properties,
      // Override with package-specific values
      origin: packageSearch.origin,
      destination: packageSearch.destination,
      infants_count: packageSearch.infants_count,
      trip_type: packageSearch.trip_type,
    },
    encryptedProperties: hotelTracking.encryptedProperties,
  };
};

/**
 * Get combined package + hotel shop tracking properties
 * Merges package-specific search data with hotel shop tracking data
 */
export const getPackageHotelShopTrackingProperties = (
  state: CombinedPackageHotelsState
) => {
  const hotelShopTracking = getHotelShopTrackingProperties(state);
  const packageSearch = getPackageSearchTrackingProperties(state);

  return {
    properties: {
      ...hotelShopTracking.properties,
      // Add package-specific context
      origin: packageSearch.origin,
      destination: packageSearch.destination,
    },
    encryptedProperties: hotelShopTracking.encryptedProperties,
  };
};

/**
 * Get combined package + hotel shop availability tracking properties
 * Merges package-specific search data with hotel shop availability tracking data
 */
export const getPackageHotelShopAvailabilityTrackingProperties = (
  state: CombinedPackageHotelsState
) => {
  const hotelShopAvailabilityTracking =
    getHotelShopAvailabilityTrackingProperties(state);
  const packageSearch = getPackageSearchTrackingProperties(state);

  return {
    properties: {
      ...hotelShopAvailabilityTracking.properties,
      // Override with package-specific values
      infants_count: packageSearch.infants_count,
    },
    encryptedProperties: hotelShopAvailabilityTracking.encryptedProperties,
  };
};

/**
 * Get package flight selection tracking properties
 * Combines package search data with flight-specific metadata
 */
export const getPackageFlightSelectionTrackingProperties = (
  state: CombinedPackageHotelsState,
  flightMetadata?: {
    fareClass?: string;
    cityPair?: string;
    tripType?: string;
  }
) => {
  const packageSearch = getPackageSearchTrackingProperties(state);

  return {
    properties: {
      ...packageSearch,
      fare_class: flightMetadata?.fareClass,
      city_pair: flightMetadata?.cityPair,
      trip_type: flightMetadata?.tripType ?? packageSearch.trip_type,
    },
  };
};

export const getPackageReviewDetailsTrackingProperties = (
  state: CombinedPackageHotelsState,
  flightMetadata?: {
    fareClass?: string;
    cityPair?: string;
    tripType?: string;
  }
) => {
  const packageSearch = getPackageSearchTrackingProperties(state);
  const hotelShop = getHotelShopTrackingProperties(state);

  return {
    properties: {
      ...packageSearch,
      fare_class: flightMetadata?.fareClass,
      city_pair: flightMetadata?.cityPair,
      trip_type: flightMetadata?.tripType ?? packageSearch.trip_type,
      lodging_id: hotelShop.properties?.lodging_id,
      lodging_name: hotelShop.properties?.name,
      city_name: hotelShop.properties?.city_name,
      country_name: hotelShop.properties?.country,
    },
    encryptedProperties: hotelShop.encryptedProperties,
  };
};
